import classNames from 'classnames';
import Image from 'next/image';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import { useState, type AnchorHTMLAttributes } from 'react';

import { CaretDown } from '@/public/assets/icons';
import { LanguageSwitcher } from '@/src/shared/presentation/components/language-switcher';



import { HeaderMobileMenu } from './header-mobile-menu';
import css from './header.css';

import type { HeaderDropdownGroup } from '../view-models/header-dropdown-group';

export function Header() {
  const { t } = useTranslation('shared');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdownGroups, setActiveDropdownGroups] = useState<HeaderDropdownGroup[]>([]);
  const onCenterDropdownLinkMouseEnter = (groups: HeaderDropdownGroup[]) => {
    setIsDropdownOpen(true);
    setActiveDropdownGroups(groups);
  };
  const onCenterDropdownLinkMouseLeave = () => {
    setIsDropdownOpen(false);
  };
  const onCenterDropdownContentMouseEnter = () => {
    setIsDropdownOpen(true);
  };
  const onCenterDropdownContentMouseLeave = () => {
    setIsDropdownOpen(false);
  };
  const onBurgerButtonClick = () => {
    setIsMobileMenuOpen(true);
  };
  const onCloseMobileMenuButtonClick = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <div className={css.wrapper}>
      {/* Desktop */}
      <nav className={classNames(css.nav, css.navDesktop)}>
        <Link href="/">
          <Image
            priority
            width={120}
            height={30}
            src="/assets/images/logo.svg"
            alt={t('components.header.logo_alt')}
          />
        </Link>

        <ul className={css.centerMenu}>
          <CenterDropdownLinkItem
            href="/alquiler-oficinas"
            label="Workplaces"
            groups={[
              [
                { label: 'Ubicaciones', href: '/alquiler-oficinas' },
                { label: 'Barcelona', href: '/alquiler-oficinas/barcelona' },
                { label: 'Madrid', href: '/alquiler-oficinas/madrid' }
              ]
            ]}
            onMouseEnter={onCenterDropdownLinkMouseEnter}
            onMouseLeave={onCenterDropdownLinkMouseLeave}
          />
          <CenterDropdownLinkItem
            href="/espacios-de-trabajo"
            label="Qué ofrecemos"
            groups={[]}
            onMouseEnter={onCenterDropdownLinkMouseEnter}
            onMouseLeave={onCenterDropdownLinkMouseLeave}
          />
          <CenterDropdownLinkItem
            href="/quienes-somos"
            label="Sobre Utopicus"
            groups={[]}
            onMouseEnter={onCenterDropdownLinkMouseEnter}
            onMouseLeave={onCenterDropdownLinkMouseLeave}
          />
          <CenterLinkItem href="/grupo-colonial" label="Grupo Colonial" />
        </ul>

        <ul className={css.rightMenu}>
          <RightLinkItem href="/contacto" label={t('components.header.contact')} />
          <RightLinkItem
            href="https://utopicus.spaces.nexudus.com/login?public&"
            label={t('components.header.login')}
            target="_blank"
          />
          <li className={css.li}>
            <LanguageSwitcher />
          </li>
        </ul>
      </nav>

      {/* Mobile */}
      <nav className={classNames(css.nav, css.navMobile)}>
        <Link href="/">
          <Image
            priority
            width={120}
            height={30}
            src="/assets/images/logo.svg"
            alt={t('components.header.logo_alt')}
          />
        </Link>

        <ul className={css.rightMenu}>
          <RightLinkItem
            href="https://utopicus.spaces.nexudus.com/login?public&"
            label={t('components.header.login')}
            target="_blank"
          />
          <li className={css.li}>
            {isMobileMenuOpen ? (
              <button className={css.closeMobileMenuButton} onClick={onCloseMobileMenuButtonClick}>
                <Image priority width={20} height={20} src="/assets/icons/close.svg" alt="" />
              </button>
            ) : (
              <button className={css.burgerButton} onClick={onBurgerButtonClick}>
                <Image priority width={20} height={20} src="/assets/icons/burger.svg" alt="" />
              </button>
            )}
          </li>
        </ul>
      </nav>

      <div
        className={css.centerMenuDropdownContent({ isOpen: isDropdownOpen })}
        onMouseEnter={onCenterDropdownContentMouseEnter}
        onMouseLeave={onCenterDropdownContentMouseLeave}
      >
        {activeDropdownGroups.map((group, index) => (
          <ul key={index} className={css.centerMenuDropdownGroup}>
            {group.map((item, itemIndex) => (
              <li key={itemIndex}>
                <Link
                  href={item.href}
                  target={item.target}
                  className={css.centerMenuDropdownGroupItemLink}
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        ))}
      </div>

      {isMobileMenuOpen && <HeaderMobileMenu />}
    </div>
  );
}

function CenterLinkItem(props: {
  href: string;
  label: string;
  target?: AnchorHTMLAttributes<HTMLAnchorElement>['target'];
}) {
  return (
    <li className={css.li}>
      <a className={css.centerLink} href={props.href} target={props.target}>
        {props.label}
      </a>
    </li>
  );
}

function CenterDropdownLinkItem(props: {
  href: string;
  label: string;
  target?: AnchorHTMLAttributes<HTMLAnchorElement>['target'];
  groups: HeaderDropdownGroup[];
  onMouseEnter?: (
    groups: HeaderDropdownGroup[],
    event: React.MouseEvent<HTMLLIElement, MouseEvent>
  ) => void;
  onMouseLeave?: (event: React.MouseEvent<HTMLLIElement, MouseEvent>) => void;
}) {
  return (
    <li
      className={css.li}
      onMouseEnter={(e) => props.onMouseEnter?.(props.groups, e)}
      onMouseLeave={props.onMouseLeave}
    >
      <a className={css.centerDropdownLink} href={props.href} target={props.target}>
        {props.label}
        <CaretDown />
      </a>
    </li>
  );
}

function RightLinkItem(props: {
  href: string;
  label: string;
  target?: AnchorHTMLAttributes<HTMLAnchorElement>['target'];
}) {
  return (
    <li className={css.li}>
      <a className={css.rightLink} href={props.href} target={props.target}>
        {props.label}
      </a>
    </li>
  );
}
